/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len

import { fromJS, List, Map, Set } from 'immutable';
import { SET_TENANT_PASS_SELECTIONS, CLEAR_COURSE_SELECTION_CACHE } from 'actions/actions';
import {
  GET_COLLECTION,
  COURSES,
} from 'actions/collections';

/*
 * Given a list of Courses, and an iterable of IDs,
 * return a list of all items that are implied by their selection
 */
export function findDependantlySelectedItems(courseList, selectedItemIds) {
  const coursesAsMap = courseList.reduce((memo, item) => memo.set(item.get('id'), item), Map());
  const selectedItemsAsSet = selectedItemIds.toSet();

  let visited = selectedItemsAsSet;
  let itemsToCheck = selectedItemsAsSet;
  let results = Set();

  while (itemsToCheck.count() > 0) {
    const dependencies = itemsToCheck
      .map(id => coursesAsMap.getIn([id, 'dependants']) || List())
      .flatten()
      .toSet();

    itemsToCheck = dependencies
      .filter(id => id && !visited.contains(id)); // eslint-disable-line no-loop-func
    results = results.concat(dependencies);
    visited = visited.concat(dependencies);
  }

  return results;
}

/*
 * Given a list of Courses, and an iterable of IDs, and whether to select,
 * return a Map of the current selection state
 */
export function setSelectionState(courseList, selectedItemIds, isSelected) {
  if (isSelected) {
    const dependants = findDependantlySelectedItems(
      courseList,
      selectedItemIds,
    );
    return Map(selectedItemIds.concat(dependants).map(id => [`courses_${id}`, isSelected]));
  }
  return Map(selectedItemIds.map(id => [`courses_${id}`, isSelected]));
}

/*
 * a reducer to store a pregenerated reverse lookup of dependencies
 */
export function courseDependencies(state = Map(), action) {
  switch (action.type) {
    case GET_COLLECTION.SUCCESS:
      if (action.meta.collectionName === COURSES) {
        return action.payload.results.reduce(
          (dependedBy, item) => {
            const immutableItem = fromJS(item);
            return (item.dependants || []).reduce(
              (dependedByInner, dependant) => dependedByInner.update(
                dependant,
                List(),
                existing => existing.push(immutableItem),
              ),
              dependedBy,
            );
          },
          Map(),
        );
      }
      return state;
    default:
      return state;
  }
}

/*
 * A reducer to cache the currently selected Courses on the CourseSelectionList,
 * as fetching them from Redux-Form causes an unacceptible performance hit
 */
export function courseSelectionCache(state = Map(), action) {
  switch (action.type) {
    case SET_TENANT_PASS_SELECTIONS:
      return state.merge(action.payload);
    case CLEAR_COURSE_SELECTION_CACHE:
      return Map();
    default:
      return state;
  }
}
